import type { Table } from 'dexie';
import { CapacitorSQLite, SQLiteConnection, SQLiteDBConnection } from '@capacitor-community/sqlite';
import {
  FileRef, collectFileRefs, deleteFile, deleteTableFiles, isFileRef, lazyBlob, lazyRefOf, readBlob, writeBlob,
} from './offline-files';

// Offline storage backends (OFFLINE_PLAN.md Phase 4, 2026-09-29).
//
// The app talks to its offline store through a small set of table operations — the ones it already used on
// Dexie (get / put / add / delete / bulkDelete / update / clear / toArray / count, a key-prefix search, a
// status or clientUuid lookup, and a JS filter). Two backends provide exactly those:
//   - DexieBackend  — today's IndexedDB store (unchanged behaviour);
//   - SqliteBackend — an SQLCipher-encrypted SQLite database (key in the iOS Keychain / Android Keystore),
//                     with photos/documents kept as private files (see offline-files.ts).
// offline-db.ts picks one at start-up and exposes it through the same `offlineDb.<table>` shape as before.

export interface TableImpl {
  get(key: any): Promise<any>;
  put(obj: any): Promise<any>;
  add(obj: any): Promise<any>;
  delete(key: any): Promise<void>;
  bulkDelete(keys: any[]): Promise<void>;
  update(key: any, changes: Record<string, any>): Promise<number>;
  clear(): Promise<void>;
  toArray(): Promise<any[]>;
  count(): Promise<number>;
  /** Rows whose PRIMARY KEY starts with prefix. */
  startsWith(prefix: string): Promise<any[]>;
  startsWithKeys(prefix: string): Promise<any[]>;
  /** Rows whose indexed `field` is one of values, in primary-key order. */
  whereIn(field: string, values: any[]): Promise<any[]>;
  whereInCount(field: string, values: any[]): Promise<number>;
  whereInDelete(field: string, values: any[]): Promise<number>;
  /** Rows passing fn. Files inside rows come back as lightweight placeholders unless fullFiles is true. */
  filter(fn: (row: any) => boolean, fullFiles: boolean): Promise<any[]>;
}

export interface StorageBackend {
  readonly kind: 'dexie' | 'sqlite';
  table(name: string): TableImpl;
}

/** Primary key of each offline table (same as the Dexie schema in offline-db.ts). */
export const TABLE_KEYS: Record<string, string> = {
  masterCache: 'key',
  dashboardSnapshot: 'key',
  detailCache: 'key',
  meta: 'key',
  outbox: 'seq',
  pendingRows: 'clientUuid',
  idMap: 'clientUuid',
  notices: 'key',
};

// ── Dexie (IndexedDB) ───────────────────────────────────────────────────────────────────────────────────────

class DexieTable implements TableImpl {
  constructor(private readonly t: Table<any, any>, private readonly pk: string) {}
  get(key: any) { return this.t.get(key); }
  put(obj: any) { return this.t.put(obj); }
  add(obj: any) { return this.t.add(obj); }
  delete(key: any) { return this.t.delete(key); }
  bulkDelete(keys: any[]) { return this.t.bulkDelete(keys); }
  update(key: any, changes: Record<string, any>) { return this.t.update(key, changes); }
  clear() { return this.t.clear(); }
  toArray() { return this.t.toArray(); }
  count() { return this.t.count(); }
  startsWith(prefix: string) { return this.t.where(this.pk).startsWith(prefix).toArray(); }
  startsWithKeys(prefix: string) { return this.t.where(this.pk).startsWith(prefix).primaryKeys(); }
  whereIn(field: string, values: any[]) { return this.t.where(field).anyOf(values).sortBy(this.pk); }
  whereInCount(field: string, values: any[]) { return this.t.where(field).anyOf(values).count(); }
  whereInDelete(field: string, values: any[]) { return this.t.where(field).anyOf(values).delete(); }
  filter(fn: (row: any) => boolean) { return this.t.filter(fn).toArray(); }
}

export class DexieBackend implements StorageBackend {
  readonly kind = 'dexie' as const;
  private readonly tables = new Map<string, DexieTable>();
  constructor(private readonly db: { table(name: string): Table<any, any> }) {}
  table(name: string): TableImpl {
    let t = this.tables.get(name);
    if (!t) {
      t = new DexieTable(this.db.table(name), TABLE_KEYS[name]);
      this.tables.set(name, t);
    }
    return t;
  }
}

// ── SQLite + SQLCipher ─────────────────────────────────────────────────────────────────────────────────────

const DB_NAME = 'll_offline_store';
const IN_CHUNK = 400; // SQLite parameter limit safety

/** Schema: one key → JSON table per store; the outbox keeps its auto-increment seq and the columns it is
 *  queried by (opId / clientUuid / status) next to the JSON. */
function schemaSql(): string {
  const kv = Object.keys(TABLE_KEYS).filter((t) => t !== 'outbox')
    .map((t) => `CREATE TABLE IF NOT EXISTS "${t}" (k TEXT PRIMARY KEY NOT NULL, v TEXT NOT NULL);`);
  return [
    ...kv,
    'CREATE TABLE IF NOT EXISTS outbox (seq INTEGER PRIMARY KEY AUTOINCREMENT, opId TEXT, clientUuid TEXT, status TEXT, v TEXT NOT NULL);',
    'CREATE INDEX IF NOT EXISTS outbox_status ON outbox(status);',
    'CREATE INDEX IF NOT EXISTS outbox_client ON outbox(clientUuid);',
  ].join('\n');
}

function randomPassphrase(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export class SqliteBackend implements StorageBackend {
  readonly kind = 'sqlite' as const;
  private readonly tables = new Map<string, SqliteTable>();
  private constructor(private readonly db: SQLiteDBConnection) {}

  /** True when this phone already has the encrypted database file. That file is the "this phone has moved" proof
   *  (OFFLINE_SQLITE_ONLY_PLAN.md) — it is checked BEFORE the old IndexedDB is touched at all. Any doubt = false. */
  static async exists(): Promise<boolean> {
    try {
      return (await new SQLiteConnection(CapacitorSQLite).isDatabase(DB_NAME)).result === true;
    } catch {
      return false;
    }
  }

  /** Opens (or creates) the encrypted store. The passphrase is generated once per phone and kept by the plugin
   *  in the iOS Keychain / Android Keystore-backed storage — it never leaves the phone. */
  static async open(): Promise<SqliteBackend> {
    const sqlite = new SQLiteConnection(CapacitorSQLite);
    if (!(await sqlite.isSecretStored()).result) {
      await sqlite.setEncryptionSecret(randomPassphrase());
    }
    const consistent = (await sqlite.checkConnectionsConsistency()).result;
    const exists = (await sqlite.isConnection(DB_NAME, false)).result;
    const db = consistent && exists
      ? await sqlite.retrieveConnection(DB_NAME, false)
      : await sqlite.createConnection(DB_NAME, true, 'secret', 1, false);
    await db.open();
    await db.execute(schemaSql(), false);
    return new SqliteBackend(db);
  }

  table(name: string): TableImpl {
    return this.sqliteTable(name);
  }

  private sqliteTable(name: string): SqliteTable {
    let t = this.tables.get(name);
    if (!t) {
      if (!TABLE_KEYS[name]) throw new Error(`Unknown offline table ${name}`);
      t = new SqliteTable(this.db, name, TABLE_KEYS[name]);
      this.tables.set(name, t);
    }
    return t;
  }

  /** One-time move from the old store: many rows in few native calls. Rows must not contain Blobs. */
  async importRows(name: string, rows: any[]): Promise<void> {
    await this.sqliteTable(name).importRows(rows);
  }

  /** Empties every table (used to undo a move that failed half-way). */
  async clearAll(): Promise<void> {
    for (const name of Object.keys(TABLE_KEYS)) await this.sqliteTable(name).clear();
  }
}

class SqliteTable implements TableImpl {
  private readonly isOutbox: boolean;
  private readonly q: string; // quoted table name

  constructor(private readonly db: SQLiteDBConnection, private readonly name: string, private readonly pk: string) {
    this.isOutbox = name === 'outbox';
    this.q = `"${name}"`;
  }

  // ── value <-> stored JSON (Blobs become private files) ──────────────────────────────────────────────────

  /** Deep copy with every Blob/File written to a file and replaced by its FileRef. A lazy placeholder (from
   *  filter) keeps the file it already points to. */
  private async toStored(value: any): Promise<any> {
    if (value instanceof Blob) {
      const kept = lazyRefOf(value);
      return kept ?? (await writeBlob(this.name, value));
    }
    if (value instanceof Date) return value.toISOString();
    if (Array.isArray(value)) {
      const out: any[] = [];
      for (const v of value) out.push(await this.toStored(v));
      return out;
    }
    if (value && typeof value === 'object') {
      const out: Record<string, any> = {};
      for (const [k, v] of Object.entries(value)) {
        if (v !== undefined) out[k] = await this.toStored(v);
      }
      return out;
    }
    return value;
  }

  /** Stored JSON back to a value, files read back as Blobs/Files (or lightweight placeholders). */
  private async fromStored(value: any, fullFiles: boolean): Promise<any> {
    if (isFileRef(value)) {
      if (!fullFiles) return lazyBlob(value);
      try { return await readBlob(value); } catch { return lazyBlob(value); }
    }
    if (Array.isArray(value)) {
      const out: any[] = [];
      for (const v of value) out.push(await this.fromStored(v, fullFiles));
      return out;
    }
    if (value && typeof value === 'object') {
      const out: Record<string, any> = {};
      for (const [k, v] of Object.entries(value)) out[k] = await this.fromStored(v, fullFiles);
      return out;
    }
    return value;
  }

  private async rowToValue(row: any, fullFiles: boolean): Promise<any> {
    const obj = await this.fromStored(JSON.parse(row.v), fullFiles);
    if (this.isOutbox) obj.seq = row.seq;
    return obj;
  }

  private async rows(sql: string, values: any[] = []): Promise<any[]> {
    const res = await this.db.query(sql, values);
    return res.values ?? [];
  }

  /** Files the previous version of a row pointed to that the new version no longer uses → deleted. */
  private async dropUnusedFiles(oldJson: string | undefined, newStored: any): Promise<void> {
    if (!oldJson) return;
    const keep = new Set(collectFileRefs(newStored).map((r) => r.__llFile));
    const gone = collectFileRefs(JSON.parse(oldJson)).filter((r) => !keep.has(r.__llFile));
    await Promise.all(gone.map((r: FileRef) => deleteFile(r)));
  }

  private async oldJson(key: any): Promise<string | undefined> {
    const col = this.isOutbox ? 'seq' : 'k';
    const found = await this.rows(`SELECT v FROM ${this.q} WHERE ${col} = ?`, [key]);
    return found[0]?.v;
  }

  // ── TableImpl ───────────────────────────────────────────────────────────────────────────────────────────

  async get(key: any): Promise<any> {
    if (key === undefined || key === null) return undefined;
    const col = this.isOutbox ? 'seq' : 'k';
    const found = await this.rows(`SELECT ${this.isOutbox ? 'seq, ' : ''}v FROM ${this.q} WHERE ${col} = ?`, [key]);
    return found.length ? this.rowToValue(found[0], true) : undefined;
  }

  async put(obj: any): Promise<any> {
    if (this.isOutbox && obj?.seq == null) return this.add(obj);
    const key = obj?.[this.pk];
    const stored = await this.toStored(obj);
    const before = await this.oldJson(key);
    const json = JSON.stringify(this.isOutbox ? { ...stored, seq: undefined } : stored);
    if (this.isOutbox) {
      await this.db.run(
        'INSERT OR REPLACE INTO outbox (seq, opId, clientUuid, status, v) VALUES (?, ?, ?, ?, ?)',
        [key, obj.opId ?? null, obj.clientUuid ?? null, obj.status ?? null, json], false);
    } else {
      await this.db.run(`INSERT OR REPLACE INTO ${this.q} (k, v) VALUES (?, ?)`, [String(key), json], false);
    }
    await this.dropUnusedFiles(before, stored);
    return key;
  }

  async add(obj: any): Promise<any> {
    if (!this.isOutbox) return this.put(obj);
    const stored = await this.toStored(obj);
    delete stored.seq;
    const res = await this.db.run(
      'INSERT INTO outbox (opId, clientUuid, status, v) VALUES (?, ?, ?, ?)',
      [obj.opId ?? null, obj.clientUuid ?? null, obj.status ?? null, JSON.stringify(stored)], false);
    return res.changes?.lastId;
  }

  async delete(key: any): Promise<void> {
    const before = await this.oldJson(key);
    await this.db.run(`DELETE FROM ${this.q} WHERE ${this.isOutbox ? 'seq' : 'k'} = ?`, [key], false);
    await this.dropUnusedFiles(before, null);
  }

  async bulkDelete(keys: any[]): Promise<void> {
    for (const key of keys) await this.delete(key);
  }

  /** Changes top-level fields in place on the STORED record — its files are left exactly as they are (a status
   *  change or "last opened" time must not read and rewrite a photo). */
  async update(key: any, changes: Record<string, any>): Promise<number> {
    const before = await this.oldJson(key);
    if (!before) return 0;
    const stored = JSON.parse(before);
    for (const [k, v] of Object.entries(changes)) {
      if (v === undefined) delete stored[k]; else stored[k] = await this.toStored(v);
    }
    const json = JSON.stringify(stored);
    if (this.isOutbox) {
      await this.db.run('UPDATE outbox SET opId = ?, clientUuid = ?, status = ?, v = ? WHERE seq = ?',
        [stored.opId ?? null, stored.clientUuid ?? null, stored.status ?? null, json, key], false);
    } else {
      await this.db.run(`UPDATE ${this.q} SET v = ? WHERE k = ?`, [json, String(key)], false);
    }
    await this.dropUnusedFiles(before, stored);
    return 1;
  }

  async clear(): Promise<void> {
    await this.db.run(`DELETE FROM ${this.q}`, [], false);
    await deleteTableFiles(this.name);
  }

  async toArray(): Promise<any[]> {
    const order = this.isOutbox ? 'seq' : 'k';
    const found = await this.rows(`SELECT ${this.isOutbox ? 'seq, ' : ''}v FROM ${this.q} ORDER BY ${order}`);
    const out: any[] = [];
    for (const r of found) out.push(await this.rowToValue(r, true));
    return out;
  }

  async count(): Promise<number> {
    const found = await this.rows(`SELECT COUNT(*) AS n FROM ${this.q}`);
    return Number(found[0]?.n ?? 0);
  }

  async startsWith(prefix: string): Promise<any[]> {
    const found = await this.rows(`SELECT v FROM ${this.q} WHERE k >= ? AND k < ? ORDER BY k`, [prefix, prefix + '￿']);
    const out: any[] = [];
    for (const r of found) out.push(await this.rowToValue(r, true));
    return out;
  }

  async startsWithKeys(prefix: string): Promise<any[]> {
    const found = await this.rows(`SELECT k FROM ${this.q} WHERE k >= ? AND k < ? ORDER BY k`, [prefix, prefix + '￿']);
    return found.map((r) => r.k);
  }

  /** Indexed lookups exist only on the outbox (status / clientUuid / opId), as in the Dexie schema. */
  private checkField(field: string): void {
    if (!this.isOutbox || !['status', 'clientUuid', 'opId'].includes(field)) {
      throw new Error(`No index ${field} on offline table ${this.name}`);
    }
  }

  async whereIn(field: string, values: any[]): Promise<any[]> {
    this.checkField(field);
    const out: any[] = [];
    for (let i = 0; i < values.length; i += IN_CHUNK) {
      const part = values.slice(i, i + IN_CHUNK);
      const found = await this.rows(
        `SELECT seq, v FROM outbox WHERE ${field} IN (${part.map(() => '?').join(',')}) ORDER BY seq`, part);
      for (const r of found) out.push(await this.rowToValue(r, true));
    }
    return out.sort((a, b) => a.seq - b.seq);
  }

  async whereInCount(field: string, values: any[]): Promise<number> {
    this.checkField(field);
    if (!values.length) return 0;
    const found = await this.rows(
      `SELECT COUNT(*) AS n FROM outbox WHERE ${field} IN (${values.map(() => '?').join(',')})`, values);
    return Number(found[0]?.n ?? 0);
  }

  async whereInDelete(field: string, values: any[]): Promise<number> {
    const rows = await this.whereIn(field, values);
    for (const r of rows) await this.delete(r.seq);
    return rows.length;
  }

  /** Bulk insert for the one-time move (see SqliteBackend.importRows) — plain JSON rows only. */
  async importRows(rows: any[]): Promise<void> {
    const CHUNK = 200;
    for (let i = 0; i < rows.length; i += CHUNK) {
      const set = rows.slice(i, i + CHUNK).map((obj) => this.isOutbox
        ? {
            statement: 'INSERT OR REPLACE INTO outbox (seq, opId, clientUuid, status, v) VALUES (?, ?, ?, ?, ?)',
            values: [obj.seq, obj.opId ?? null, obj.clientUuid ?? null, obj.status ?? null, JSON.stringify({ ...obj, seq: undefined })],
          }
        : {
            statement: `INSERT OR REPLACE INTO ${this.q} (k, v) VALUES (?, ?)`,
            values: [String(obj[this.pk]), JSON.stringify(obj)],
          });
      await this.db.executeSet(set, true);
    }
  }

  async filter(fn: (row: any) => boolean, fullFiles: boolean): Promise<any[]> {
    const order = this.isOutbox ? 'seq' : 'k';
    const found = await this.rows(`SELECT ${this.isOutbox ? 'seq, ' : ''}v FROM ${this.q} ORDER BY ${order}`);
    const out: any[] = [];
    for (const r of found) {
      const light = await this.rowToValue(r, false);
      if (!fn(light)) continue;
      // A kept row is returned with its real files when asked (e.g. a queued photo shown as a preview).
      out.push(fullFiles ? await this.rowToValue(r, true) : light);
    }
    return out;
  }
}
